import { useEffect, useState } from "react";
import { Coins, RefreshCw, Plus, Save, Loader2, X, ToggleLeft, ToggleRight } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useFx } from "../lib/fx";

export default function AdminCurrenciesPanel() {
  const { reload: reloadFx } = useFx();
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null);
  const [editing, setEditing] = useState(null); // currency code being edited
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setError(null);
    try {
      const { data } = await api.get("/admin/currencies");
      setItems(data.items || []);
    } catch (e) { setError(formatErr(e)); }
  };

  useEffect(() => { reload(); }, []);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(null), 4000); };

  const refreshRates = async () => {
    setBusy("refresh"); setError(null);
    try {
      const { data } = await api.post("/admin/currencies/refresh-rates");
      showFlash(`Refreshed ${data.updated} rates from CoinMarketCap.${data.errors?.length ? " Errors: " + data.errors.join("; ") : ""}`);
      reload(); reloadFx();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  const toggleEnabled = async (c) => {
    setBusy(c.code);
    try {
      await api.patch(`/admin/currencies/${c.code}`, { is_enabled: !c.is_enabled });
      reload(); reloadFx();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-currencies-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="eyebrow flex items-center gap-2 text-teal"><Coins size={12}/> Currencies & FX</div>
          <h2 className="font-serif-display text-3xl mt-1">Money</h2>
          <p className="text-sm text-muted mt-1 max-w-xl">EUR is the base. CoinMarketCap auto-refreshes crypto + USD every 15 min. Set manual rates for custom tokens like TAL or MINT.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={refreshRates} disabled={busy === "refresh"} className="btn btn-outline" data-testid="btn-refresh-fx">
            {busy === "refresh" ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Refresh from CMC
          </button>
          <button onClick={() => setCreating(true)} className="btn btn-primary" data-testid="btn-add-currency">
            <Plus size={14}/> New currency
          </button>
        </div>
      </div>

      {error && <div className="text-rose-300 text-sm mb-3">{error}</div>}
      {flash && <div className="luxe-card p-3 text-sm text-teal border-teal/40 mb-4" data-testid="ccy-flash">{flash}</div>}

      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.code} className={cx("luxe-card p-4 flex items-center gap-4 flex-wrap",
                                          !c.is_enabled && "opacity-60")} data-testid={`ccy-row-${c.code}`}>
            <div className="flex items-center gap-3 flex-1 min-w-[180px]">
              {c.icon_url ? (
                <img src={c.icon_url} alt="" className="h-10 w-10 rounded-full object-cover"/>
              ) : (
                <div className="h-10 w-10 rounded-full bg-teal/10 grid place-items-center text-teal font-mono">
                  {c.symbol || c.code[0]}
                </div>
              )}
              <div>
                <div className="font-mono text-sm flex items-center gap-2">
                  {c.code}
                  {c.is_base && <span className="chip success !text-[10px] !py-0">BASE</span>}
                  <span className={cx("chip !text-[10px] !py-0", c.kind === "fiat" ? "" : c.kind === "crypto" ? "info" : "success")}>{c.kind}</span>
                </div>
                <div className="text-xs text-muted">{c.name}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-right">
                <div className="font-serif-display text-base">{(c.rate_to_base || 0).toFixed(c.kind === "crypto" && c.rate_to_base > 100 ? 2 : 6)} EUR</div>
                <div className="text-[10px] text-muted/70 font-mono">
                  {c.rate_source}
                  {c.rate_updated_at && ` · ${new Date(c.rate_updated_at).toLocaleTimeString()}`}
                </div>
              </div>
              <button onClick={() => toggleEnabled(c)} disabled={c.is_base || busy === c.code}
                      className="text-muted hover:text-teal transition disabled:opacity-30"
                      title={c.is_enabled ? "Disable" : "Enable"}
                      data-testid={`ccy-toggle-${c.code}`}>
                {c.is_enabled ? <ToggleRight size={20} className="text-teal"/> : <ToggleLeft size={20}/>}
              </button>
              <button onClick={() => setEditing(c.code)} className="btn btn-ghost text-[12px]" data-testid={`ccy-edit-${c.code}`}>
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {(creating || editing) && (
        <CurrencyForm
          currency={editing ? items.find((c) => c.code === editing) : null}
          onClose={(refresh) => { setCreating(false); setEditing(null); if (refresh) { reload(); reloadFx(); showFlash(editing ? "Currency updated." : "Currency created."); } }}
        />
      )}
    </div>
  );
}

function CurrencyForm({ currency, onClose }) {
  const isEdit = !!currency;
  const [form, setForm] = useState(() => currency ? {
    code: currency.code, name: currency.name, symbol: currency.symbol || "",
    kind: currency.kind, decimals: currency.decimals, is_enabled: currency.is_enabled,
    manual_rate: currency.manual_rate ?? "", rate_source: currency.rate_source,
    icon_url: currency.icon_url || "", sort_order: currency.sort_order,
    redeem_enabled: currency.redeem_enabled ?? false,
    redeem_rate_eur: currency.redeem_rate_eur ?? "",
    redeem_max_percent: currency.redeem_max_percent ?? "",
  } : {
    code: "", name: "", symbol: "", kind: "crypto", decimals: 2, is_enabled: true,
    manual_rate: "", rate_source: "coinmarketcap", icon_url: "", sort_order: 99,
    redeem_enabled: false, redeem_rate_eur: 0.01, redeem_max_percent: 50,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload = {
        ...form,
        manual_rate: form.manual_rate === "" ? null : Number(form.manual_rate),
        decimals: Number(form.decimals),
        sort_order: Number(form.sort_order),
        redeem_rate_eur: form.redeem_rate_eur === "" ? null : Number(form.redeem_rate_eur),
        redeem_max_percent: form.redeem_max_percent === "" ? null : Number(form.redeem_max_percent),
      };
      if (isEdit) {
        delete payload.code;
        await api.patch(`/admin/currencies/${currency.code}`, payload);
      } else {
        await api.post(`/admin/currencies`, payload);
      }
      onClose(true);
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm" onClick={() => !busy && onClose(false)}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="w-full md:max-w-lg max-h-[95svh] overflow-y-auto modal-panel p-6 space-y-4"
            data-testid="ccy-form">
        <div className="flex items-start justify-between">
          <h3 className="font-serif-display text-2xl">{isEdit ? `Edit ${currency.code}` : "New currency"}</h3>
          <button type="button" onClick={() => onClose(false)} className="text-muted hover:text-bone"><X size={20}/></button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="block col-span-1">
            <span className="label-tiny block mb-1">Code</span>
            <input required maxLength={10} value={form.code} disabled={isEdit}
                   onChange={(e) => set("code", e.target.value.toUpperCase())}
                   className="input-bare font-mono uppercase" placeholder="MATIC" data-testid="ccy-code"/>
          </label>
          <label className="block col-span-2">
            <span className="label-tiny block mb-1">Name</span>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-bare" data-testid="ccy-name"/>
          </label>
          <label className="block">
            <span className="label-tiny block mb-1">Symbol</span>
            <input maxLength={5} value={form.symbol} onChange={(e) => set("symbol", e.target.value)} className="input-bare" placeholder="$"/>
          </label>
          <label className="block">
            <span className="label-tiny block mb-1">Kind</span>
            <select value={form.kind} onChange={(e) => set("kind", e.target.value)} className="input-bare">
              <option value="fiat">fiat</option>
              <option value="crypto">crypto</option>
              <option value="points">points</option>
            </select>
          </label>
          <label className="block">
            <span className="label-tiny block mb-1">Decimals</span>
            <input type="number" min="0" max="18" value={form.decimals} onChange={(e) => set("decimals", e.target.value)} className="input-bare"/>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label-tiny block mb-1">Rate source</span>
            <select value={form.rate_source} onChange={(e) => set("rate_source", e.target.value)} className="input-bare" data-testid="ccy-source">
              <option value="coinmarketcap">CoinMarketCap auto</option>
              <option value="manual">Manual override</option>
            </select>
          </label>
          <label className="block">
            <span className="label-tiny block mb-1">{form.rate_source === "manual" ? "Manual rate (EUR per unit)" : "Initial rate"}</span>
            <input type="number" step="any" min="0" value={form.manual_rate} onChange={(e) => set("manual_rate", e.target.value)} className="input-bare" data-testid="ccy-rate"/>
          </label>
        </div>
        <label className="block">
          <span className="label-tiny block mb-1">Icon URL (optional)</span>
          <input value={form.icon_url} onChange={(e) => set("icon_url", e.target.value)} className="input-bare font-mono text-xs" placeholder="/favicon.ico or https://…"/>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label-tiny block mb-1">Sort order</span>
            <input type="number" min="0" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="input-bare"/>
          </label>
          <label className="flex items-end gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={form.is_enabled} onChange={(e) => set("is_enabled", e.target.checked)} className="h-4 w-4 accent-teal mb-3"/>
            <span className="mb-3">Enabled</span>
          </label>
        </div>

        {form.kind === "points" && (
          <div className="luxe-card p-4 border-teal/30 space-y-3" data-testid="ccy-redeem-section">
            <div className="eyebrow text-teal">Redemption at checkout</div>
            <p className="text-xs text-muted">
              Allow guests to redeem these points against booking deposits.
            </p>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={form.redeem_enabled}
                     onChange={(e) => set("redeem_enabled", e.target.checked)}
                     className="h-4 w-4 accent-teal" data-testid="ccy-redeem-enabled"/>
              <span>Enable redemption</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="label-tiny block mb-1">Rate — EUR per 1 point</span>
                <input type="number" step="0.0001" min="0" value={form.redeem_rate_eur}
                       onChange={(e) => set("redeem_rate_eur", e.target.value)}
                       className="input-bare" placeholder="0.01"
                       data-testid="ccy-redeem-rate"/>
              </label>
              <label className="block">
                <span className="label-tiny block mb-1">Max % of booking</span>
                <input type="number" step="1" min="0" max="100" value={form.redeem_max_percent}
                       onChange={(e) => set("redeem_max_percent", e.target.value)}
                       className="input-bare" placeholder="50"
                       data-testid="ccy-redeem-max-percent"/>
              </label>
            </div>
          </div>
        )}

        {error && <div className="text-rose-300 text-sm">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => onClose(false)} className="btn btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn btn-primary" data-testid="ccy-save">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
          </button>
        </div>
      </form>
    </div>
  );
}
